import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getTrialBalanceComparison } from "./trialBalanceComparisonService";
import TrialBalanceComparisonTable from "./TrialBalanceComparisonTable";
import {
  FaCalendarAlt,
  FaFileInvoiceDollar,
  FaChartLine,
  FaArrowLeft,
  FaBalanceScale,
  FaCheckCircle,
  FaExclamationTriangle,
  FaSync,
  FaSlidersH,
} from "react-icons/fa";

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const TrialBalanceComparisonPage = () => {
  const navigate = useNavigate();

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter options
  const [numYears, setNumYears] = useState(5);
  const [balanceMode, setBalanceMode] = useState("cumulative"); // 'cumulative' | 'annual'
  const [viewMode, setViewMode] = useState("net"); // 'net' | 'dual'

  const fetchReport = useCallback(async (nYears, mode) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getTrialBalanceComparison(nYears, mode);
      setReportData(data);
    } catch (err) {
      setError("Failed to load Trial Balance comparison report. Please try again.");
      console.error("Trial balance comparison error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReport(numYears, balanceMode);
  }, [numYears, balanceMode, fetchReport]);

  const handleYearsChange = (n) => {
    setNumYears(n);
  };

  const handleModeChange = (mode) => {
    setBalanceMode(mode);
  };

  const latestYear = reportData?.latest_year;
  const latestSummary = latestYear ? reportData?.summary?.[latestYear] : null;
  const isLatestBalanced = latestSummary?.is_balanced;

  return (
    <div className="container-fluid py-4 px-3 px-md-4" style={{ background: "#f8fafc", minHeight: "100vh" }}>
      {/* ── Page Header ──────────────────────────────────────────────────── */}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
        <div className="d-flex align-items-center gap-3">
          <div
            className="p-3 rounded-3 d-flex align-items-center justify-content-center"
            style={{ background: "#e0f2fe", color: "#0284c7" }}
          >
            <FaBalanceScale size={28} />
          </div>
          <div>
            <h3 className="mb-0 fw-bold text-dark">Trial Balance 5-Year Comparison</h3>
            <small className="text-muted">
              Multi-year side-by-side comparison of ledger accounts, debits, credits, and net balances
            </small>
          </div>
        </div>

        {/* Quick Navigations */}
        <div className="d-flex flex-wrap align-items-center gap-2">
          <button
            className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1 shadow-sm"
            onClick={() => navigate("/dashboard/reports/trial-balance")}
          >
            <FaFileInvoiceDollar /> Standard Trial Balance
          </button>
          <button
            className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1 shadow-sm"
            onClick={() => navigate("/dashboard/reports/balance-sheet-comparison")}
          >
            <FaBalanceScale /> Balance Sheet Comparison
          </button>
          <button
            className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1 shadow-sm"
            onClick={() => navigate("/dashboard/reports/pnl-comparison")}
          >
            <FaChartLine /> P&amp;L Comparison
          </button>
          <button
            className="btn btn-secondary btn-sm d-flex align-items-center gap-1 shadow-sm"
            onClick={() => navigate("/dashboard")}
          >
            <FaArrowLeft /> Dashboard
          </button>
        </div>
      </div>

      {/* ── KPI Cards ────────────────────────────────────────────────────── */}
      {latestSummary && (
        <div className="row g-3 mb-4">
          <div className="col-12 col-sm-6 col-xl-3">
            <div className="card shadow-sm border-0 border-start border-4 border-info h-100 p-3 bg-white">
              <div className="text-uppercase text-muted fw-semibold small">Active Accounts</div>
              <div className="fs-3 fw-bold text-dark mt-1">
                {reportData?.accounts?.length || 0}
              </div>
              <small className="text-muted">In Chart of Accounts</small>
            </div>
          </div>

          <div className="col-12 col-sm-6 col-xl-3">
            <div className="card shadow-sm border-0 border-start border-4 border-success h-100 p-3 bg-white">
              <div className="text-uppercase text-muted fw-semibold small">
                Total Debits (FY {latestYear})
              </div>
              <div className="fs-3 fw-bold text-success mt-1">
                ${fmt(latestSummary.total_debit)}
              </div>
              <small className="text-muted">Value inflows to accounts</small>
            </div>
          </div>

          <div className="col-12 col-sm-6 col-xl-3">
            <div className="card shadow-sm border-0 border-start border-4 border-danger h-100 p-3 bg-white">
              <div className="text-uppercase text-muted fw-semibold small">
                Total Credits (FY {latestYear})
              </div>
              <div className="fs-3 fw-bold text-danger mt-1">
                ${fmt(latestSummary.total_credit)}
              </div>
              <small className="text-muted">Value outflows from accounts</small>
            </div>
          </div>

          <div className="col-12 col-sm-6 col-xl-3">
            <div
              className={`card shadow-sm border-0 border-start border-4 ${
                isLatestBalanced ? "border-success" : "border-danger"
              } h-100 p-3 bg-white`}
            >
              <div className="text-uppercase text-muted fw-semibold small">
                FY {latestYear} Balance Status
              </div>
              <div className="fs-3 fw-bold mt-1 d-flex align-items-center gap-2">
                {isLatestBalanced ? (
                  <span className="text-success d-flex align-items-center gap-1">
                    <FaCheckCircle size={22} /> Balanced
                  </span>
                ) : (
                  <span className="text-danger d-flex align-items-center gap-1">
                    <FaExclamationTriangle size={22} /> Diff: ${fmt(Math.abs(latestSummary.difference || 0))}
                  </span>
                )}
              </div>
              <small className="text-muted">Debits equal Credits verification</small>
            </div>
          </div>
        </div>
      )}

      {/* ── Filter / Period Controls Bar ──────────────────────────────────── */}
      <div className="card shadow-sm border-0 mb-4 bg-white">
        <div className="card-body py-3 d-flex flex-wrap justify-content-between align-items-center gap-3">
          {/* Horizon Selection */}
          <div className="d-flex flex-wrap align-items-center gap-3">
            <div className="d-flex align-items-center gap-2">
              <FaCalendarAlt className="text-primary" />
              <label className="form-label mb-0 fw-semibold text-secondary small">
                Horizon:
              </label>
              <div className="btn-group" role="group">
                {[2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`btn btn-sm ${
                      numYears === n ? "btn-primary fw-bold" : "btn-outline-primary"
                    }`}
                    onClick={() => handleYearsChange(n)}
                  >
                    {n} Years
                  </button>
                ))}
              </div>
            </div>

            {/* Mode: Cumulative vs Annual */}
            <div className="d-flex align-items-center gap-2 border-start ps-3">
              <FaSlidersH className="text-secondary small" />
              <label className="form-label mb-0 fw-semibold text-secondary small">
                Balance Mode:
              </label>
              <div className="btn-group" role="group">
                <button
                  type="button"
                  className={`btn btn-sm ${
                    balanceMode === "cumulative" ? "btn-dark fw-semibold" : "btn-outline-secondary"
                  }`}
                  onClick={() => handleModeChange("cumulative")}
                  title="Cumulative balance up to each fiscal year end"
                >
                  Cumulative Year-End
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${
                    balanceMode === "annual" ? "btn-dark fw-semibold" : "btn-outline-secondary"
                  }`}
                  onClick={() => handleModeChange("annual")}
                  title="Activity strictly within each fiscal year (Jan 1 - Dec 31)"
                >
                  Annual Activity
                </button>
              </div>
            </div>

            {/* View Mode: Net vs Dual Dr/Cr */}
            <div className="d-flex align-items-center gap-2 border-start ps-3">
              <label className="form-label mb-0 fw-semibold text-secondary small">
                Columns:
              </label>
              <div className="btn-group" role="group">
                <button
                  type="button"
                  className={`btn btn-sm ${
                    viewMode === "net" ? "btn-info text-white fw-semibold" : "btn-outline-info"
                  }`}
                  onClick={() => setViewMode("net")}
                >
                  Net Balance
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${
                    viewMode === "dual" ? "btn-info text-white fw-semibold" : "btn-outline-info"
                  }`}
                  onClick={() => setViewMode("dual")}
                >
                  Dual (Dr / Cr)
                </button>
              </div>
            </div>
          </div>

          {/* Refresh button */}
          <div>
            <button
              className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
              onClick={() => fetchReport(numYears, balanceMode)}
              disabled={loading}
            >
              <FaSync className={loading ? "fa-spin" : ""} /> Refresh
            </button>
          </div>
        </div>
      </div>

      {/* ── Content Area ─────────────────────────────────────────────────── */}
      {loading ? (
        <div className="card shadow-sm p-5 text-center my-4 bg-white">
          <div className="spinner-border text-primary mx-auto mb-3" role="status" />
          <h5 className="text-secondary fw-semibold">Loading Trial Balance Comparison…</h5>
          <p className="text-muted small mb-0">
            Fetching ledger entries and rolling up hierarchical accounts across {numYears} fiscal years
          </p>
        </div>
      ) : error ? (
        <div className="alert alert-danger d-flex justify-content-between align-items-center">
          <div>{error}</div>
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={() => fetchReport(numYears, balanceMode)}
          >
            Retry
          </button>
        </div>
      ) : !reportData || !reportData.years || reportData.years.length === 0 ? (
        <div className="alert alert-warning">No financial data available for comparison.</div>
      ) : (
        <TrialBalanceComparisonTable reportData={reportData} viewMode={viewMode} />
      )}
    </div>
  );
};

export default TrialBalanceComparisonPage;
